<template>
	<div id="xpo-api-form">
		<div class="form-intro mb-5" v-if="config.resource === 'registrations'">
			<h2>
				Please note: this is a <strong>TRADE ONLY</strong> event.<br />Children under 14 are strictly not
				permitted.
			</h2>
			<p>Please complete one form per person attending the show.</p>
		</div>

		<div id="form_step1" class="form-step form-step-step1" data-step="step1" style="">
			<ValidationObserver ref="validation" slim>
				<form method="post" @submit.prevent="onSubmit">
					<template v-for="(group, index) in fieldGroups">
						<transition name="slide" :key="index">
							<div :class="{ 'card mb-2': group.layout === 'card' }">
								<div class="card-header" v-if="group.layout === 'card'">
									<p class="label m-0">
										<strong>{{ group.title }}</strong>
									</p>
								</div>
								<div :class="{ 'card-body': group.layout === 'card' }">
									<div class="row" v-if="evaluateRenderConditions(group.conditions)">
										<div v-for="(field, name) in group.fields" :key="name" :class="field.wrapper">
											<div class="field-component">
												<!-- Note a key for the component is required in order for the transition to work when component doesn't change -->
												<transition name="slide">
													<component
														v-if="evaluateRenderConditions(field.conditions)"
														:key="field.index ? name + '-' + field.index : name"
														:is="field.component"
														v-model="answers[name]"
														v-bind="{
															label: processReplacementTokens(field.label),
															...(Object.prototype.hasOwnProperty.call(
																field,
																'validationLabel'
															)
																? { validationLabel: processReplacementTokens(field.validationLabel) }
																: {}),
															...(Object.prototype.hasOwnProperty.call(field, 'options')
																? { options: processIterableReplacementTokens(field.options) }
																: {}),
															...(Object.prototype.hasOwnProperty.call(field, 'limit')
																? { limit: field.limit }
																: {}),
															...(Object.prototype.hasOwnProperty.call(field, 'required')
																? { required: field.required }
																: {}),
															...(Object.prototype.hasOwnProperty.call(field, 'immediate')
																? { immediate: field.immediate }
																: {}),
															...(Object.prototype.hasOwnProperty.call(
																field,
																'checkedValue'
															)
																? { checkedValue: field.checkedValue }
																: {}),
															...(Object.prototype.hasOwnProperty.call(
																field,
																'uncheckedValue'
															)
																? { uncheckedValue: field.uncheckedValue }
																: {}),
														}"
														@input="handleResets(field)"
													>
														<span v-html="processReplacementTokens(field.slot)" v-if="field.slot"></span>
													</component>
												</transition>
											</div>
										</div>
									</div>
								</div>
							</div>
						</transition>
					</template>

					<!-- <div class="alert alert-secondary">
						<div class="uk-flex uk-flex-middle uk-text-bold">A late registration fee of $25 applies.</div>
						<div class="uk-flex uk-flex-middle uk-text-bold">&nbsp;</div>
						<div class="uk-flex uk-flex-middle uk-text-bold">To complete your registration, please continue
							to the next
							page to process payment.</div>
					</div> -->

					<button
						type="submit"
						class="btn btn-primary"
						:disabled="isSubmitting"
						v-on:click="$event.target.blur()"
					>
						{{ submitText }}
					</button>
				</form>
			</ValidationObserver>
		</div>
	</div>
</template>

<script>
/*global SIGNUP_FORM_DATA, SUCCESS_URL*/
import * as Sentry from '@sentry/browser';
import Swal from 'sweetalert2';
import { ValidationObserver } from 'vee-validate';

import server from './Mixins/server';
import textInput from './Components/TextInput';
import radioInputGroup from './Components/RadioInputGroup';
import radioInput from './Components/RadioInput';
import checkInputGroup from './Components/CheckInputGroup';
import checkInput from './Components/CheckInput';
import addressFinderBundle from './Components/AddressFinderBundle';
import exhibitorBadges from './Components/ExhibitorBadges';
import { FormValidationException, ApiResponseErrorException } from './Utils/exceptions';

export default {
	name: 'xpo-api-form',
	mixins: [server],

	created() {
		//  Pre-populate form data if editing in regadmin
		if (typeof SIGNUP_FORM_DATA === 'object') {
			this.data = SIGNUP_FORM_DATA;

			// Set registration ID if editing in regadmin
			if (Object.prototype.hasOwnProperty.call(SIGNUP_FORM_DATA, 'id')) {
				this.id = SIGNUP_FORM_DATA.id;
			}
		}
	},

	props: {
		// Gets passed in when this root vue component is mounted
		config: Object,
	},

	data() {
		// Set up initial empty answers object from config using specified default values or null
		let answers = this.resetAnswers();

		return {
			answers: answers,
			id: null, // Will be set if editing a resource
			isSubmitting: false,
		};
	},

	methods: {
		// Handle form submission;
		async onSubmit() {
			try {
				/**
				 * VeeValidate provides a way to only call this `onSubmit` method when validations passes,
				 * Ie:
				 *
				 * <ValidationObserver v-slot="{ handleSubmit }">
				 * 		<form @onSubmit.prevent="handleSubmit(onSubmit)">
				 * 			...
				 * 		</form>
				 * </ValidationObserver>
				 *
				 * But since we want to show a SweetAlert, and have access to the failed validations,
				 * instead we assign a ref to the `ValidationObserver`, so that we can call `validate()`
				 * manually in this method, and throw/catch an exception to handle the SweetAlert logic.
				 */
				let isValid = await this.$refs.validation.validate();
				if (!isValid) {
					throw new FormValidationException();
				}

				this.isSubmitting = true;

				// Handle [POST]
				if (!this.id) {
					let res = await this.post(this.config.resource, this.config.showId, this.data);
					let { data } = res;

					if (!data.success) {
						throw new ApiResponseErrorException(res);
					}

					let swalConfig = {
						icon: 'success',
						title: 'Thank You',
						html: this.config.successMessage
					};

					// Add configuration for new entry button using configured text if specified
					if (Object.prototype.hasOwnProperty.call(this.config, 'newEntryButtonText')) {
						swalConfig = {
							...swalConfig, 
							...{
								confirmButtonText: this.config.newEntryButtonText,
								// Enabled by default
								showCancelButton: 'showCancelButton' in this.config ? this.config.showCancelButton : true,
								// Enabled by default
								showConfirmButton: 'showConfirmButton' in this.config ? this.config.showConfirmButton : true,
								focusConfirm: false,
								cancelButtonText: 'Close'
							}
						};
					}

					Swal.fire(swalConfig).then(({ isConfirmed }) => {
						// Reset only the new entry fields if there is a new entry button and it was pressed
						// And focus the first field, scroll to the top and reset form validation
						if (Object.prototype.hasOwnProperty.call(this.config, 'newEntryButtonText') && isConfirmed) {
							this.resetAnswers(true);
							document.querySelector('#xpo-api-form input').focus();
							this.$refs.validation.reset();

							// Delay so scroll doesn't glitch due to changes in DOM (presumably)
							window.setTimeout(function() {
								window.scrollTo(0, 0);
							}, 500);
						}
						// Otherwise go to the success URL if it was set
						else if (data.success && typeof SUCCESS_URL !== 'undefined') {
							window.location.href = SUCCESS_URL;
						}
						// Otherwise reset all the fields, scroll to the top and reset form validation
						else {
							this.resetAnswers();
							this.$refs.validation.reset();
							
							// Delay so scroll doesn't glitch due to changes in DOM (presumably)
							window.setTimeout(function () {
								window.scrollTo(0, 0);
							}, 500);
						}
					});
				}
				// Handle [PUT]
				else {
					let res = await this.put(this.config.resource, this.config.showId, this.id, this.data);
					let { data } = res;
					if (!data.success) {
						throw new ApiResponseErrorException(res);
					}
					if (data.success) {
						history.scrollRestoration = 'manual'; // Prevent browser from retaining scroll position when reloading
						window.location.reload(true);
					}
				}

				this.isSubmitting = false;
			} catch (e) {
				switch (e.constructor) {
					case FormValidationException:
						Swal.fire(
							'Error',
							`
								<p>${e.message}</p>
								<p>
									${Object.keys(this.$refs.validation.errors)
										.filter((key) => this.$refs.validation.errors[key].length)
										.join('<br>')}
								</p>
							`,
							'error'
						);
						break;
					case ApiResponseErrorException:
						Swal.fire('Error', e.message, 'error');

						Sentry.configureScope(function(scope) {
							scope.setExtra('response', e.res);
							Sentry.captureMessage('API Response Error');
						});
						break;
					default:

						// Handle axios errors
						// https://stackabuse.com/handling-errors-with-axios/
						if (e.response) {
							Swal.fire(
								'Server Error',
								'A server error has occurred.<br><br>Please try again later or contact us on<br>09 976 8300 or onsite at the show.',
								'error'
							);
						}
						else if (e.request) {
							Swal.fire(
								'Network Error',
								'Looks like you’re having trouble submitting this form (and we think this is due to your internet connection).<br><br>Feel free to try again later or contact us on<br>09 976 8300 or onsite at the show.',
								'error'
							);
						}
						else {
							Swal.fire(
								'Unknown Error',
								'An unknwon error has occurred.<br><br>Please try again later or contact us on<br>09 976 8300 or onsite at the show.',
								'error'
							);
						}

						Sentry.captureException(e);
				}

				this.isSubmitting = false;
			}
		},

		// Determine if a field or group shuld be rendered
		// Returns true if the specified conditions are met, otherwise false
		evaluateRenderConditions(conditions) {
			if (!conditions) {
				return true;
			}

			let render = true;

			// Loop through each condition and determine if any of them fail (therefore this should not render)
			for (let field in conditions) {
				// If the field to check is an array then test if it includes the condition value
				if (Array.isArray(this.answers[field])) {
					render = this.answers[field].includes(conditions[field]);
				}
				// Otherwise just compare the value of the field to check
				else {
					// If the CONDITION VALUE is an array then test if the field to check is equal to one of the values
					if (Array.isArray(conditions[field])) {
						render = conditions[field].includes(this.answers[field]);
					}
					// Otherwise just do a direct string comparison
					else {
						render = this.answers[field] === conditions[field];
					}
				}
			}

			return render;
		},

		// Reset the specified field values when the field in question changes
		handleResets(field) {
			if (!field.reset) {
				return false;
			}

			// Loop through each field that should be reset
			for (const resetField of field.reset) {
				// Loop through each field group to find the field we're resetting
				// Note we're using the computed field groups so fields with multiple question sets are handled
				for (const group of this.fieldGroups) {
					// Reset the answer to the default value for the field we're resetting
					// And break out of the for loop to move onto the next field to reset
					if (Object.prototype.hasOwnProperty.call(group.fields, resetField)) {
						this.answers[resetField] = this.defaultValue(group.fields[resetField]);
						break;
					}
				}
			}
		},

		// Resets the answers on component data (if exists) and returns them in order to set default values when component data doesn't exist yet
		// Pass true to only reset fields configured to be reset on new entries
		resetAnswers(newEntry = false) {
			// Note that answers is passed by reference so if it exists, we'll intentionally modify it in data below through this var
			// If it doesn't exist then we'll just pass back the newly created object
			let answers = typeof this.answers !== 'undefined' ? this.answers : {};

			// Note we're looping through the original form config rather than the computed one since it won't exist yet the first time around
			// This means that array fields (where there are multiple question sets for the same question) will not have properties
			// E.g. field.component will be undefined when defaulting the value, but that's okay because this just means it defaults to null
			for (const group of this.config.form) {
				for (const field in group.fields) {
					// Reset the field to its default value if this is a not a 'new entry' reset
					// Or if it is, then only if the field is has been configured to reset for new entries
					if (
						!newEntry ||
						(Object.prototype.hasOwnProperty.call(group.fields[field], 'newEntryReset') &&
							group.fields[field].newEntryReset === true)
					) {
						answers[field] = this.defaultValue(group.fields[field]);
					}
				}
			}

			return answers;
		},

		// Return default value for the specified field
		defaultValue(field) {
			// Manually specified default value overrides all
			if (Object.prototype.hasOwnProperty.call(field, 'defaultValue')) {
				return field.defaultValue;
			}

			// Type-based default value
			switch (field.component) {
				// Check input groups should always be arrays
				case 'check-input-group':
					return [];

				// Address finder bundle should always start with empty NZ address
				case 'address-finder-bundle':
					return {
						full_address: null,
						street: null,
						address1: null,
						suburb: null,
						city: null,
						postcode: null,
						country: 'New Zealand',
					};
			}

			// Default to a null default value
			return null;
		},

		// Replace custom replacement tokens
		processReplacementTokens(string) {
			return string
				.replaceAll('{show}', this.config.show)
				.replaceAll('{termsUrl}', this.config.termsUrl);
		},

		// Iterate over array and run replacement token replacements on each item accordingly
		// This could later be extended to work with key/value objects too
		processIterableReplacementTokens(iterable) {
			return Array.isArray(iterable) ? iterable.map(this.processReplacementTokens) : iterable;
		}
	},

	computed: {
		// Convert field arrays (where there are multiple choices for which component to render depending on a condition)
		// into the current field that matches the condition
		fieldGroups() {
			// Take copy of groups so we're not modifying the original config object
			let groups = JSON.parse(JSON.stringify(this.config.form));

			return groups.map((group) => {
				for (let field in group.fields) {
					if (Array.isArray(group.fields[field])) {
						let index = group.fields[field].findIndex((conditionalField) =>
							this.evaluateRenderConditions(conditionalField.conditions)
						);
						group.fields[field] = index !== -1 ? group.fields[field][index] : group.fields[field][0];

						// Add an index to array fields so we can use it as a key to uniquely identify components for transitioning
						group.fields[field]['index'] = index;
					}
				}

				return group;
			});
		},

		// Process data for sending to server
		data: {
			// For sending answer data to database
			get() {
				// Get copy of answers
				let data = JSON.parse(JSON.stringify(this.answers));

				// Flatten address data into main data and remove the original object
				data = { ...data, ...data.address };
				delete data.address;

				return data;
			},

			// For setting answer data from database
			set(data) {
				// Only merge in properties that exist in answers
				Object.keys(data).forEach((key) => {
					// Merge in properties that exist on answers object
					if (key in this.answers) {
						this.answers[key] = data[key];
					}

					// Merge in properties that exist on address object
					if (key in this.answers.address) {
						this.answers.address[key] = data[key];
					}
				});
			},
		},

		submitText() {
			if (this.id !== null) {
				return this.isSubmitting === true ? 'Saving...' : 'Save';
			}

			return this.isSubmitting === true ? 'Submitting...' : this.config.submitButtonText;
		},
	},

	components: {
		ValidationObserver,
		textInput,
		radioInputGroup,
		radioInput,
		checkInputGroup,
		checkInput,
		addressFinderBundle,
		exhibitorBadges,
	},
};
</script>

<style lang="scss">
// So we can scroll the user up smoothly after completing the form
html {
	scroll-behavior: smooth;
}

// Fix dialogues being tiny on other sites
.swal2-popup {
	font-size: 1.6rem;
}

// Remove pluses being added to AddressFinder dropdown by sites like BuildNZ
ul.af_list li.af_item:before {
	content: '';
}

#xpo-api-form {
	@import '~bootstrap/scss/bootstrap';
	@import './Styles/form.scss';

	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	color: #666;
	font-size: 15px;
	line-height: 22px;
	font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
	text-align: left;

	// Prevent jumping during slide animation
	.field-component > .form-group {
		margin-bottom: 0;
	}
	.field-component:not(:empty) {
		margin-bottom: 30px;
	}
	.no-margin .field-component {
		margin-bottom: 0;
	}

	// Slide animation
	.slide-enter-active {
		transition-duration: 0.3s;
	}

	.slide-leave-active {
		transition-duration: 0.3s;
	}

	.slide-enter-to,
	.slide-leave {
		max-height: 500px;
		overflow-y: hidden;
	}

	.slide-enter,
	.slide-leave-to {
		overflow-y: hidden;
		max-height: 0;
	}

	.radio-group-wrapper.is-invalid-wrapper .label,
	.exhibitor-badge-wrapper.is-invalid-wrapper .label {
		color: #dc3545;
	}
}
</style>
